import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { getSensitiveTopicLabel, getSensitiveUILabel } from '../utils/sensitiveTopicsTranslations';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft,
  Globe,
  TrendingUp,
  Link as LinkIcon,
  Tag,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Clock,
  Mail,
  Heart,
  Share2,
  BarChart3,
  Languages,
  MapPin,
  Loader2,
  Info,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Helper functions
const formatTraffic = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
  return num.toString();
};

const getDRColor = (dr) => {
  if (dr >= 70) return 'text-success';
  if (dr >= 50) return 'text-primary';
  if (dr >= 30) return 'text-warning';
  return 'text-muted-foreground';
};

const getDRBg = (dr) => {
  if (dr >= 70) return 'bg-success/10 border-success/20';
  if (dr >= 50) return 'bg-primary/10 border-primary/20';
  if (dr >= 30) return 'bg-warning/10 border-warning/20';
  return 'bg-muted border-border';
};

export const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const texts = {
    es: {
      notFound: 'Producto no encontrado',
      backToCatalog: 'Volver al Catálogo',
      home: 'Inicio',
      catalog: 'Catálogo',
      about: 'Información del sitio',
      specifications: 'Especificaciones',
      price: 'Precio Venta',
      priceSensitive: 'Precio Sensible',
      traffic: 'Tráfico Orgánico',
      country: 'País',
      language: 'Idioma',
      linkType: 'Tipo de Enlace',
      linkDuration: 'Duración del Enlace',
      siteType: 'Tipo de Sitio',
      adLabel: 'Etiqueta',
      sensitiveTopics: 'Temáticas Sensibles',
      accepts: 'Acepta',
      notAccepts: 'No acepta temáticas sensibles',
      available: 'Disponible',
      notAvailable: 'No disponible',
      contact: 'Contactar',
      addToList: 'Añadir a Lista',
      visitSite: 'Visitar sitio',
      yes: 'Sí',
      no: 'No',
    },
    en: {
      notFound: 'Product not found',
      backToCatalog: 'Back to Catalog',
      home: 'Home',
      catalog: 'Catalog',
      about: 'Site Information',
      specifications: 'Specifications',
      price: 'Sale Price',
      priceSensitive: 'Sensitive Price',
      traffic: 'Organic Traffic',
      country: 'Country',
      language: 'Language',
      linkType: 'Link Type',
      linkDuration: 'Link Duration',
      siteType: 'Site Type',
      adLabel: 'Label',
      sensitiveTopics: 'Sensitive Topics',
      accepts: 'Accepts',
      notAccepts: "Doesn't accept sensitive topics",
      available: 'Available',
      notAvailable: 'Not available',
      contact: 'Contact',
      addToList: 'Add to List',
      visitSite: 'Visit site',
      yes: 'Yes',
      no: 'No',
    }
  };

  const t = texts[language] || texts.es;

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      setProduct(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">{t.notFound}</h1>
            <Button asChild>
              <Link to="/">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t.backToCatalog}
              </Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Build sensitive topics list with translations
  const sensitiveTopics = [];
  if (product.casino) sensitiveTopics.push(getSensitiveTopicLabel('casino', language));
  if (product.cbd) sensitiveTopics.push(getSensitiveTopicLabel('cbd', language));
  if (product.gambling) sensitiveTopics.push(getSensitiveTopicLabel('gambling', language));
  if (product.cryptocurrency) sensitiveTopics.push(getSensitiveTopicLabel('cryptocurrency', language));
  if (product.erotic) sensitiveTopics.push(getSensitiveTopicLabel('erotic', language));
  if (product.sex) sensitiveTopics.push(getSensitiveTopicLabel('sex', language));
  if (product.porn) sensitiveTopics.push(getSensitiveTopicLabel('porn', language));
  if (product.vpn) sensitiveTopics.push(getSensitiveTopicLabel('vpn', language));
  if (product.tarot) sensitiveTopics.push(getSensitiveTopicLabel('tarot', language));
  if (product.cannabis) sensitiveTopics.push(getSensitiveTopicLabel('cannabis', language));
  const acceptsSensitive = sensitiveTopics.length > 0;

  const handleContact = () => {
    toast.success(language === 'es' ? 'Solicitud de contacto enviada' : 'Contact request sent');
  };

  const handleAddToList = () => {
    toast.success(language === 'es' ? 'Añadido a tu lista' : 'Added to your list');
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success(language === 'es' ? 'Enlace copiado al portapapeles' : 'Link copied to clipboard');
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1">
        {/* Breadcrumb */}
        <div className="bg-muted/30 border-b border-border">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">
                {t.home}
              </Link>
              <span className="text-muted-foreground">/</span>
              <Link to="/#catalog" className="text-muted-foreground hover:text-primary transition-colors">
                {t.catalog}
              </Link>
              <span className="text-muted-foreground">/</span>
              <span className="text-foreground font-medium">{product.website}</span>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          {/* Back Button */}
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 -ml-2">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t.backToCatalog}
          </Button>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Header Card */}
              <Card className="overflow-hidden">
                <div className="bg-hero-gradient p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="h-12 w-12 rounded-xl bg-primary-foreground/10 flex items-center justify-center">
                          <Globe className="h-6 w-6 text-primary-foreground" />
                        </div>
                        <div>
                          <h1 className="text-2xl sm:text-3xl font-bold text-primary-foreground">
                            {product.website}
                          </h1>
                          <p className="text-primary-foreground/70">{product.category || '-'}</p>
                        </div>
                      </div>
                    </div>
                    <div className={`px-4 py-2 rounded-xl border-2 ${getDRBg(product.dr)} bg-primary-foreground`}>
                      <span className={`text-3xl font-bold ${getDRColor(product.dr)}`}>
                        {product.dr || 0}
                      </span>
                      <span className="text-sm text-muted-foreground block text-center">DR</span>
                    </div>
                  </div>

                  {/* Availability Badge */}
                  {!product.available && (
                    <Badge variant="destructive" className="mt-4">
                      {t.notAvailable}
                    </Badge>
                  )}
                </div>

                {product.more_info && (
                  <CardContent className="p-6 sm:p-8">
                    <h2 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Info className="h-4 w-4 text-primary" />
                      {t.about}
                    </h2>
                    <p className="text-muted-foreground leading-relaxed">{product.more_info}</p>
                  </CardContent>
                )}
              </Card>

              {/* Specifications Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    {t.specifications}
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid sm:grid-cols-2 gap-4">
                  {/* Prices */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-accent/5 border border-accent/20">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-accent/10 flex items-center justify-center">
                        <span className="text-accent font-bold">€</span>
                      </div>
                      <span className="text-muted-foreground">{t.price}</span>
                    </div>
                    <span className="text-2xl font-bold text-accent">€{product.price || 0}</span>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-warning/5 border border-warning/20">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-warning/10 flex items-center justify-center">
                        <span className="text-warning font-bold">€</span>
                      </div>
                      <span className="text-muted-foreground">{t.priceSensitive}</span>
                    </div>
                    <span className="text-2xl font-bold text-warning">€{product.price_sensitive || 0}</span>
                  </div>

                  {/* Traffic */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-mint-light border border-mint/20">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-mint/20 flex items-center justify-center">
                        <TrendingUp className="h-5 w-5 text-mint-dark" />
                      </div>
                      <span className="text-muted-foreground">{t.traffic}</span>
                    </div>
                    <span className="text-2xl font-bold text-mint-dark">
                      {formatTraffic(product.organic_traffic)}
                    </span>
                  </div>

                  {/* Country */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-muted-foreground">{t.country}</span>
                    </div>
                    <span className="font-semibold text-foreground">{product.country || '-'}</span>
                  </div>

                  {/* Language */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-secondary/20 flex items-center justify-center">
                        <Languages className="h-5 w-5 text-secondary" />
                      </div>
                      <span className="text-muted-foreground">{t.language}</span>
                    </div>
                    <span className="font-semibold text-foreground">{product.language || '-'}</span>
                  </div>

                  {/* Link Type */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <LinkIcon className="h-5 w-5 text-primary" />
                      </div>
                      <span className="text-muted-foreground">{t.linkType}</span>
                    </div>
                    <Badge variant="secondary">{product.link_type || '-'}</Badge>
                  </div>

                  {/* Link Duration */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                        <Calendar className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <span className="text-muted-foreground">{t.linkDuration}</span>
                    </div>
                    <span className="font-semibold text-foreground">{product.link_duration || '-'}</span>
                  </div>

                  {/* Site Type */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                        <Globe className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <span className="text-muted-foreground">{t.siteType}</span>
                    </div>
                    <span className="font-semibold text-foreground text-right max-w-[150px] truncate">
                      {product.site_type || '-'}
                    </span>
                  </div>

                  {/* Ad Label */}
                  {product.ad_label && (
                    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border sm:col-span-2">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-warning/10 flex items-center justify-center">
                          <Tag className="h-5 w-5 text-warning" />
                        </div>
                        <span className="text-muted-foreground">{t.adLabel}</span>
                      </div>
                      <Badge className="bg-warning/10 text-warning border-warning/30">
                        {product.ad_label}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sensitive Topics Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {acceptsSensitive ? (
                      <CheckCircle2 className="h-5 w-5 text-success" />
                    ) : (
                      <XCircle className="h-5 w-5 text-muted-foreground" />
                    )}
                    {t.sensitiveTopics}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {acceptsSensitive ? (
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">{t.accepts}:</p>
                      <div className="flex flex-wrap gap-2">
                        {sensitiveTopics.map((topic) => (
                          <Badge key={topic} variant="secondary" className="px-3 py-1.5">
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">{t.notAccepts}</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* CTA Card */}
              <Card className="sticky top-24">
                <CardContent className="p-6 space-y-4">
                  <div className="text-center pb-4 border-b border-border">
                    <span className="text-sm text-muted-foreground">{t.price}</span>
                    <p className="text-4xl font-bold text-accent">€{product.price || 0}</p>
                    {product.price_sensitive > 0 && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {t.priceSensitive}: <span className="text-warning font-semibold">€{product.price_sensitive}</span>
                      </p>
                    )}
                  </div>

                  {product.available ? (
                    <>
                      <Button className="w-full" size="lg" onClick={handleContact}>
                        <Mail className="mr-2 h-5 w-5" />
                        {t.contact}
                      </Button>

                      <Button variant="outline" className="w-full" size="lg" onClick={handleAddToList}>
                        <Heart className="mr-2 h-5 w-5" />
                        {t.addToList}
                      </Button>
                    </>
                  ) : (
                    <Badge variant="destructive" className="w-full justify-center py-2">
                      {t.notAvailable}
                    </Badge>
                  )}

                  <Separator />

                  <Button variant="ghost" className="w-full" onClick={handleShare}>
                    <Share2 className="mr-2 h-4 w-4" />
                    Share
                  </Button>

                  <Button variant="ghost" className="w-full" asChild>
                    <a href={`https://${product.website}`} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      {t.visitSite}
                    </a>
                  </Button>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Quick Stats</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Domain Rating</span>
                    <span className={`font-semibold ${getDRColor(product.dr)}`}>{product.dr || 0}/100</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Organic Traffic</span>
                    <span className="font-semibold text-foreground">{formatTraffic(product.organic_traffic)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Link Type</span>
                    <span className="font-semibold text-foreground">{product.link_type || '-'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Sensitive Topics</span>
                    <span className="font-semibold text-foreground">{sensitiveTopics.length}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
